/**
 * PDF Caching utility using IndexedDB
 * Caches PDF files locally to avoid re-downloading on every page visit
 */

const DB_NAME = 'vidya-pdf-cache';
const DB_VERSION = 1;
const STORE_NAME = 'pdfs';

// Cache expiry time (7 days in milliseconds)
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000;

interface CachedPDF {
  bookId: string;
  blob: Blob;
  cachedAt: number;
  url: string; // Original URL for cache invalidation
}

/**
 * Open the IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open PDF cache database');
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'bookId' });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };
  });
}

/**
 * Get a cached PDF by book ID
 */
export async function getCachedPDF(bookId: string): Promise<Blob | null> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(bookId);

      request.onerror = () => {
        db.close();
        resolve(null);
      };

      request.onsuccess = () => {
        db.close();
        const cached = request.result as CachedPDF | undefined;
        
        if (!cached) {
          resolve(null);
          return;
        }

        // Check if cache has expired
        const now = Date.now();
        if (now - cached.cachedAt > CACHE_EXPIRY) {
          // Cache expired, delete it
          deleteCachedPDF(bookId).catch(console.error);
          resolve(null);
          return;
        }

        resolve(cached.blob);
      };
    });
  } catch (error) {
    console.error('Error getting cached PDF:', error);
    return null;
  }
}

/**
 * Cache a PDF blob
 */
export async function cachePDF(bookId: string, blob: Blob, originalUrl: string): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const data: CachedPDF = {
        bookId,
        blob,
        cachedAt: Date.now(),
        url: originalUrl,
      };
      
      const request = store.put(data);

      request.onerror = () => {
        db.close();
        console.error('Failed to cache PDF');
        reject(request.error);
      };

      request.onsuccess = () => {
        db.close();
        console.log(`PDF cached for book ${bookId}`);
        resolve();
      };
    });
  } catch (error) {
    console.error('Error caching PDF:', error);
  }
}

/**
 * Delete a cached PDF
 */
export async function deleteCachedPDF(bookId: string): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(bookId);

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

      request.onsuccess = () => {
        db.close();
        resolve();
      };
    });
  } catch (error) {
    console.error('Error deleting cached PDF:', error);
  }
}

/**
 * Clear all cached PDFs (for cache management)
 */
export async function clearAllCachedPDFs(): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

      request.onsuccess = () => {
        db.close();
        console.log('All cached PDFs cleared');
        resolve();
      };
    });
  } catch (error) {
    console.error('Error clearing PDF cache:', error);
  }
}

/**
 * Get total cache size in bytes
 */
export async function getCacheSize(): Promise<number> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onerror = () => {
        db.close();
        resolve(0);
      };

      request.onsuccess = () => {
        db.close();
        const items = request.result as CachedPDF[];
        const totalSize = items.reduce((sum, item) => sum + item.blob.size, 0);
        resolve(totalSize);
      };
    });
  } catch (error) {
    console.error('Error getting cache size:', error);
    return 0;
  }
}

/**
 * Clean up expired cache entries
 */
export async function cleanupExpiredCache(): Promise<void> {
  try {
    const db = await openDB();
    const now = Date.now();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

      request.onsuccess = () => {
        const items = request.result as CachedPDF[];
        const expiredIds = items
          .filter(item => now - item.cachedAt > CACHE_EXPIRY)
          .map(item => item.bookId);
        
        // Delete expired entries
        expiredIds.forEach(id => store.delete(id));
        
        transaction.oncomplete = () => {
          db.close();
          if (expiredIds.length > 0) {
            console.log(`Cleaned up ${expiredIds.length} expired PDF cache entries`);
          }
          resolve();
        };
      };
    });
  } catch (error) {
    console.error('Error cleaning up PDF cache:', error);
  }
}

/**
 * Fetch PDF and cache it, or return cached version
 */
export async function fetchAndCachePDF(bookId: string, pdfUrl: string): Promise<string> {
  // First, check if we have a cached version
  const cachedBlob = await getCachedPDF(bookId);

  if (cachedBlob) {
    console.log(`Using cached PDF for book ${bookId}`);
    // Create a blob URL from the cached blob
    return URL.createObjectURL(cachedBlob);
  }

  // Not cached, fetch from URL
  console.log(`Fetching PDF for book ${bookId} from server...`);

  try {
    const response = await fetch(pdfUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.status}`);
    }

    const blob = await response.blob();

    // Cache the blob for future use (async, don't wait)
    cachePDF(bookId, blob, pdfUrl).catch(console.error);

    // Return blob URL
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching PDF:', error);
    // Fall back to original URL if fetching fails
    return pdfUrl;
  }
}

/**
 * Progress information for PDF loading
 */
export interface PDFLoadProgress {
  loaded: number;      // Bytes loaded
  total: number;       // Total bytes (from Content-Length header)
  percentage: number;  // 0-100
}

/**
 * Fetch PDF with progress tracking and cache it, or return cached version
 */
export async function fetchAndCachePDFWithProgress(
  bookId: string,
  pdfUrl: string,
  onProgress?: (progress: PDFLoadProgress) => void
): Promise<string> {
  // First, check if we have a cached version
  const cachedBlob = await getCachedPDF(bookId);

  if (cachedBlob) {
    console.log(`Using cached PDF for book ${bookId}`);
    // Report 100% immediately for cached files
    onProgress?.({ loaded: cachedBlob.size, total: cachedBlob.size, percentage: 100 });
    return URL.createObjectURL(cachedBlob);
  }

  // Not cached, fetch from URL with progress tracking
  console.log(`Fetching PDF for book ${bookId} from server...`);

  try {
    const response = await fetch(pdfUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.status}`);
    }

    // Get total size from Content-Length header
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;

    if (!response.body) {
      // Fallback if ReadableStream not available
      const blob = await response.blob();
      onProgress?.({ loaded: blob.size, total: blob.size, percentage: 100 });
      cachePDF(bookId, blob, pdfUrl).catch(console.error);
      return URL.createObjectURL(blob);
    }

    // Stream the response and track progress
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      loaded += value.length;

      // Report progress
      const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;
      onProgress?.({ loaded, total, percentage });
    }

    // Combine chunks into a single Blob
    const blob = new Blob(chunks, { type: 'application/pdf' });

    // Cache the blob for future use (async, don't wait)
    cachePDF(bookId, blob, pdfUrl).catch(console.error);

    // Final progress report
    onProgress?.({ loaded: blob.size, total: blob.size, percentage: 100 });

    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching PDF:', error);
    // Fall back to original URL if fetching fails
    return pdfUrl;
  }
}




